import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { PortalConfigurationType, VendorProfile } from '@trnsact/trnsact-shared-types';
import { TabsOption } from 'global';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { useRoutesVisibilityChecks } from 'hooks/useRoutesVisibilityChecks';
import { FinancePerformancePage } from 'modules/finance/performance';
import { ProposalsPage } from 'modules/proposals';
import { FinanceList } from '../components';
import { SharedFinanceFilters, INITIAL_SHARED_FINANCE_FILTERS } from 'modules/finance/sharedFilters';

type FinancePageTab = 'finance-log' | 'performance' | 'proposals';

export const FinanceListPage = () => {
  const { isDealerUser, isLenderUser, isCommonPartner, isAftermarketPartner } = useContext(CommonDataContext);
  const { replace } = useHistory();
  const location = useLocation();
  const vendorProfile = useSelector((state: any) => state.vp) as VendorProfile;
  const opportunitiesConfiguration = useSelector(
    (state: any) => state.portalConfiguration?.[PortalConfigurationType.Opportunities]
  );
  const { checkIfProposalsHubVisible } = useRoutesVisibilityChecks();
  const isDealerPortalConfigurationReady = !isDealerUser || Boolean(opportunitiesConfiguration);

  const [sharedFilters, setSharedFilters] = useState<SharedFinanceFilters>(INITIAL_SHARED_FINANCE_FILTERS);

  const isProposalsVisible =
    isDealerPortalConfigurationReady &&
    checkIfProposalsHubVisible(isCommonPartner, isAftermarketPartner, isDealerUser, isLenderUser, vendorProfile);

  useEffect(() => {
    if (isDealerPortalConfigurationReady && location.hash === '#proposals' && !isProposalsVisible) {
      replace({ pathname: location.pathname, search: location.search, hash: '#performance' });
    }
  }, [
    isDealerPortalConfigurationReady,
    isProposalsVisible,
    location.hash,
    location.pathname,
    location.search,
    replace,
  ]);

  const tabs: TabsOption<FinancePageTab>[] = [
    {
      value: 'performance',
      label: 'Performance',
      urlAnchor: '#performance',
      component: <FinancePerformancePage sharedFilters={sharedFilters} onSharedFiltersChange={setSharedFilters} />,
    },
    {
      value: 'finance-log',
      label: 'Finance Log',
      urlAnchor: '#finance-log',
      component: (
        <CardContainer>
          <FinanceList sharedFilters={sharedFilters} onSharedFiltersChange={setSharedFilters} />
        </CardContainer>
      ),
    },
    {
      value: 'proposals',
      label: 'Proposals',
      urlAnchor: '#proposals',
      component: <ProposalsPage />,
      isVisible: isProposalsVisible,
    },
  ];

  return (
    <PageContainer title="Finance">
      <TabsContainer<FinancePageTab> tabs={tabs} isSyncWithAnchor />
    </PageContainer>
  );
};
